export type Suit = "♠" | "♥" | "♦" | "♣";

export interface Card {
  id: string;
  suit: Suit;
  rank: number;
  label: string;
  value: number;
}

export interface CardSlot {
  card: Card;
  knownToOwner: boolean;
}

export interface GolfPlayer {
  id: string;
  peerId: string;
  name: string;
  avatar: string;
  accent: string;
  isHost: boolean;
  cards: CardSlot[];
  knocked: boolean;
  connected: boolean;
}

export type GamePhase = "lobby" | "playing" | "finished";

export interface PendingDraw {
  playerId: string;
  card: Card;
  source: "deck" | "discard";
}

export interface InternalGolfState {
  roomCode: string;
  status: GamePhase;
  hostPeerId: string;
  hostPlayerId: string;
  players: GolfPlayer[];
  turnIndex: number;
  deck: Card[];
  discardPile: Card[];
  pending: PendingDraw | null;
  knockPlayerId: string | null;
  remainingTurnsAfterKnock: number | null;
  lastMessage: string;
}

export interface PlayerScore {
  playerId: string;
  score: number;
}

export interface ClientCardView {
  rank: number;
  suit: Suit;
  label: string;
  visible: boolean;
  value: number;
}

export interface GolfPlayerClientView {
  id: string;
  name: string;
  avatar: string;
  accent: string;
  isHost: boolean;
  connected: boolean;
  knocked: boolean;
  cards: ClientCardView[];
  score: number | null;
}

export interface GolfClientState {
  roomCode: string;
  status: GamePhase;
  players: GolfPlayerClientView[];
  turnPlayerId: string | null;
  drawnCardForMe: Card | null;
  deckCount: number;
  discardTop: Card | null;
  discardCount: number;
  pendingPlayerId: string | null;
  knockPlayerId: string | null;
  remainingTurnsAfterKnock: number | null;
  lastMessage: string;
}

export interface PeekedCard {
  playerId: string;
  index: number;
  label: string;
  rank: number;
  suit: Suit;
}

export const AVATAR_OPTIONS = [
  "🌸",
  "🍀",
  "🌷",
  "⛳",
  "🏌️",
  "💌",
  "🦋",
  "☘️",
];

export const ACCENT_OPTIONS = [
  "#f9c5d1",
  "#e8d7ff",
  "#d8f5e3",
  "#ffe0cc",
  "#d9c7f4",
  "#ffd6e8",
  "#d9f0ff",
  "#f4d9ff",
];

export const MAX_PLAYERS = 4;

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

const SUITS: Suit[] = ["♠", "♥", "♦", "♣"];

function cardLabel(rank: number): string {
  switch (rank) {
    case 1:
      return "A";
    case 11:
      return "J";
    case 12:
      return "Q";
    case 13:
      return "K";
    default:
      return String(rank);
  }
}

function cardValue(rank: number): number {
  if (rank === 12) {
    return -5;
  }
  return rank;
}

function makeCard(suit: Suit, rank: number): Card {
  return {
    id: makeId(),
    suit,
    rank,
    label: cardLabel(rank),
    value: cardValue(rank),
  };
}

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function createDeck(): Card[] {
  return shuffle(
    SUITS.flatMap((suit) =>
      Array.from({ length: 13 }, (_, index) => makeCard(suit, index + 1)),
    ),
  );
}

function scoreCards(cards: CardSlot[]): number {
  return cards.reduce((acc, slot) => acc + slot.card.value, 0);
}

function cardToClientView(
  cardSlot: CardSlot,
  isMine: boolean,
  revealAll: boolean,
): ClientCardView {
  if (revealAll || (isMine && cardSlot.knownToOwner)) {
    return {
      rank: cardSlot.card.rank,
      suit: cardSlot.card.suit,
      label: cardSlot.card.label,
      visible: true,
      value: cardSlot.card.value,
    };
  }

  return {
    rank: cardSlot.card.rank,
    suit: cardSlot.card.suit,
    label: "?",
    visible: false,
    value: cardSlot.card.value,
  };
}

function createFreshDeckPile() {
  return createDeck();
}

export function cloneState(state: InternalGolfState): InternalGolfState {
  return {
    ...state,
    players: state.players.map((player) => ({
      ...player,
      cards: player.cards.map((slot) => ({
        ...slot,
        card: { ...slot.card },
      })),
    })),
    deck: state.deck.map((card) => ({ ...card })),
    discardPile: state.discardPile.map((card) => ({ ...card })),
    pending: state.pending
      ? {
          ...state.pending,
          card: { ...state.pending.card },
        }
      : null,
  };
}

export function makeRoomCode(): string {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join(
    "",
  );
}

export function createPlayer(
  name: string,
  avatar: string,
  accent: string,
  peerId: string,
  isHost: boolean,
): GolfPlayer {
  return {
    id: makeId(),
    peerId,
    name,
    avatar,
    accent,
    isHost,
    cards: Array.from({ length: 4 }, () => ({
      card: makeCard("♠", 1),
      knownToOwner: false,
    })),
    knocked: false,
    connected: true,
  };
}

export function createLobbyState(roomCode: string, hostPeerId: string): InternalGolfState {
  return {
    roomCode,
    status: "lobby",
    hostPeerId,
    hostPlayerId: "",
    players: [],
    turnIndex: 0,
    deck: createFreshDeckPile(),
    discardPile: [],
    pending: null,
    knockPlayerId: null,
    remainingTurnsAfterKnock: null,
    lastMessage: "Waiting for players",
  };
}

export function addPlayer(
  state: InternalGolfState,
  name: string,
  avatar: string,
  accent: string,
  peerId: string,
  isHost = false,
): { state: InternalGolfState; playerId: string } {
  if (state.players.length >= MAX_PLAYERS) {
    throw new Error("Room is full.");
  }

  if (state.players.some((p) => p.peerId === peerId)) {
    throw new Error("You already joined this room.");
  }

  const player = createPlayer(name, avatar, accent, peerId, isHost);
  const players = [...state.players, player];

  return {
    state: {
      ...state,
      players,
      hostPlayerId: isHost ? player.id : state.hostPlayerId,
      lastMessage: `${player.name} joined the room.`,
    },
    playerId: player.id,
  };
}

export function removePlayer(state: InternalGolfState, playerId: string): InternalGolfState {
  const players = state.players.filter((player) => player.id !== playerId);
  if (state.hostPlayerId === playerId) {
    const newHostId = players[0]?.id ?? "";
    return {
      ...state,
      hostPlayerId: newHostId,
      players,
      hostPeerId: newHostId
        ? players[0]?.peerId ?? state.hostPeerId
        : state.hostPeerId,
      lastMessage: "Host left the room.",
    };
  }

  return {
    ...state,
    players,
    lastMessage: "A player left the room.",
  };
}

function ensureHostTurn(state: InternalGolfState, playerId: string): void {
  const currentPlayer = state.players[state.turnIndex];
  if (!currentPlayer || currentPlayer.id !== playerId) {
    throw new Error("It's not your turn.");
  }
}

function ensureActivePlayerPending(state: InternalGolfState, playerId: string): PendingDraw {
  if (state.status !== "playing") {
    throw new Error("Game is not active.");
  }

  const pending = state.pending;
  if (!pending || pending.playerId !== playerId) {
    throw new Error("No drawn card is waiting for this player.");
  }

  return pending;
}

function nextTurnState(state: InternalGolfState): InternalGolfState {
  if (state.status !== "playing") {
    return state;
  }

  const nextPlayerIndex = (state.turnIndex + 1) % state.players.length;

  if (state.knockPlayerId) {
    const remaining = (state.remainingTurnsAfterKnock ?? 0) - 1;
    if (remaining <= 0) {
      return {
        ...state,
        status: "finished",
        knockPlayerId: null,
        remainingTurnsAfterKnock: null,
        pending: null,
        lastMessage: "Round complete. Final scores are ready.",
      };
    }

    return {
      ...state,
      remainingTurnsAfterKnock: remaining,
      turnIndex: nextPlayerIndex,
      lastMessage: `${state.players[nextPlayerIndex]?.name ?? "The next player"}'s turn. ${remaining} pick${
        remaining === 1 ? "" : "s"
      } left.`,
    };
  }

  return {
    ...state,
    turnIndex: nextPlayerIndex,
    lastMessage: `${state.players[nextPlayerIndex]?.name ?? "The next player"}'s turn.`,
  };
}

function drawCardFromDeck(state: InternalGolfState): Card | null {
  let deck = [...state.deck];
  const discard = [...state.discardPile];

  if (deck.length === 0) {
    if (discard.length <= 1) {
      return null;
    }
    const keepTop = discard.pop();
    if (!keepTop) {
      return null;
    }
    deck = shuffle(discard);
    const drawn = deck.pop();
    if (!drawn) {
      return null;
    }
    state.deck = deck;
    state.discardPile = [keepTop];
    return drawn;
  }

  const drawn = deck.pop() ?? null;
  state.deck = deck;
  return drawn;
}

export function dealIfNeeded(state: InternalGolfState): InternalGolfState {
  const players = state.players.map((player) => ({
    ...player,
    cards: [] as CardSlot[],
    knocked: false,
  }));

  const deck = [...createFreshDeckPile()];

  players.forEach((player) => {
    player.cards = [];
    for (let i = 0; i < 4; i += 1) {
      const card = deck.pop();
      if (!card) {
        throw new Error("Not enough cards to deal.");
      }
      player.cards.push({ card, knownToOwner: i < 2 });
    }
  });

  const discardSeed = deck.pop();
  const discardPile: Card[] = discardSeed ? [discardSeed] : [];

  return {
    ...state,
    deck,
    discardPile,
    players,
  };
}

export function canStartGame(state: InternalGolfState): boolean {
  return state.status === "lobby" && state.players.length >= 2;
}

export function startGame(state: InternalGolfState): InternalGolfState {
  if (!canStartGame(state)) {
    throw new Error("Need at least two players to start.");
  }
  if (state.status !== "lobby") {
    throw new Error("Game has already started.");
  }

  const nextState = dealIfNeeded(state);

  return {
    ...nextState,
    status: "playing",
    turnIndex: 0,
    pending: null,
    knockPlayerId: null,
    remainingTurnsAfterKnock: null,
    lastMessage: "Round started. Lowest score wins.",
  };
}

export function drawFromSource(
  state: InternalGolfState,
  playerId: string,
  source: "deck" | "discard",
): InternalGolfState {
  if (state.status !== "playing") {
    throw new Error("Game has not started.");
  }
  ensureHostTurn(state, playerId);

  if (state.pending) {
    throw new Error("You already have a drawn card. Replace or discard first.");
  }

  const player = state.players[state.turnIndex];
  if (!player || player.id !== playerId) {
    throw new Error("You're not the active player.");
  }

  let card: Card | null = null;

  if (source === "deck") {
    card = drawCardFromDeck(state);
    if (!card) {
      throw new Error("No cards in draw pile.");
    }
  } else {
    const top = state.discardPile.pop();
    if (!top) {
      throw new Error("Discard pile is empty.");
    }
    card = top;
  }

  return {
    ...state,
    pending: {
      playerId,
      card,
      source,
    },
    lastMessage: `${player.name} drew from ${source}.`,
  };
}

function replaceCardAtIndex(
  state: InternalGolfState,
  playerId: string,
  index: number,
): InternalGolfState {
  const pending = ensureActivePlayerPending(state, playerId);

  const players = [...state.players];
  const player = players[state.turnIndex];
  if (!player) {
    throw new Error("Active player not found.");
  }
  if (index < 0 || index > 3) {
    throw new Error("Choose a valid card position.");
  }

  const targetSlot = player.cards[index];
  const replaced = targetSlot.card;

  const updatedCards = [...player.cards];
  updatedCards[index] = {
    ...updatedCards[index],
    card: pending.card,
    knownToOwner: true,
  };

  players[state.turnIndex] = {
    ...player,
    cards: updatedCards,
  };

  return {
    ...state,
    players,
    discardPile: [...state.discardPile, replaced],
    pending: null,
    lastMessage: `${player.name} swapped a card.`,
  };
}

export function replaceDrawnCard(
  state: InternalGolfState,
  playerId: string,
  index: number,
): InternalGolfState {
  const nextState = replaceCardAtIndex(state, playerId, index);
  return nextTurnState(nextState);
}

export function discardDrawnCard(
  state: InternalGolfState,
  playerId: string,
): InternalGolfState {
  const pending = ensureActivePlayerPending(state, playerId);

  return nextTurnState({
    ...state,
    pending: null,
    discardPile: [...state.discardPile, pending.card],
    lastMessage: "Player discarded their drawn card.",
  });
}

export function matchAndDiscard(
  state: InternalGolfState,
  playerId: string,
  index: number,
): InternalGolfState {
  const pending = ensureActivePlayerPending(state, playerId);

  const players = [...state.players];
  const playerIndex = players.findIndex((entry) => entry.id === playerId);
  if (playerIndex === -1) {
    throw new Error("Player not found.");
  }

  const player = players[playerIndex];
  if (index < 0 || index > 3) {
    throw new Error("Choose a valid card position.");
  }

  const targetCard = player.cards[index];
  if (targetCard.card.rank !== pending.card.rank) {
    throw new Error("Match-and-discard requires equal ranks.");
  }

  const replacement = drawCardFromDeck(state);
  if (!replacement) {
    throw new Error("No cards in deck for match-and-discard replacement.");
  }

  const updatedCards = [...player.cards];
  updatedCards[index] = {
    card: replacement,
    knownToOwner: true,
  };

  players[playerIndex] = {
    ...player,
    cards: updatedCards,
  };

  const nextDiscard = [...state.discardPile, pending.card, targetCard.card];

  return nextTurnState({
    ...state,
    players,
    discardPile: nextDiscard,
    pending: null,
    lastMessage: `${player.name} used match-and-discard with a ${targetCard.card.label}.`,
  });
}

export function jackSwapWithPlayer(
  state: InternalGolfState,
  playerId: string,
  targetPlayerId: string,
  myIndex: number,
  theirIndex: number,
): InternalGolfState {
  const pending = ensureActivePlayerPending(state, playerId);
  if (pending.card.rank !== 11) {
    throw new Error("Jack power card is required for swap.");
  }

  const myIdx = state.players.findIndex((entry) => entry.id === playerId);
  const theirIdx = state.players.findIndex((entry) => entry.id === targetPlayerId);

  if (myIdx === -1 || theirIdx === -1) {
    throw new Error("Players not found for swap.");
  }

  const myPlayer = state.players[myIdx];
  const theirPlayer = state.players[theirIdx];

  if (
    myIndex < 0 ||
    myIndex > 3 ||
    theirIndex < 0 ||
    theirIndex > 3
  ) {
    throw new Error("Choose valid card positions for swap.");
  }

  const myCards = [...myPlayer.cards];
  const theirCards = [...theirPlayer.cards];

  [myCards[myIndex], theirCards[theirIndex]] = [
    theirCards[theirIndex],
    myCards[myIndex],
  ];

  const updatedPlayers = [...state.players];
  updatedPlayers[myIdx] = { ...myPlayer, cards: myCards };
  updatedPlayers[theirIdx] = { ...theirPlayer, cards: theirCards };

  return nextTurnState({
    ...state,
    players: updatedPlayers,
    discardPile: [...state.discardPile, pending.card],
    pending: null,
    lastMessage: `${myPlayer.name} swapped with ${theirPlayer.name} using Jack.`,
  });
}

export function revealOwnCard(
  state: InternalGolfState,
  playerId: string,
  index: number,
): InternalGolfState {
  const players = state.players.map((entry) => {
    if (entry.id !== playerId) {
      return entry;
    }

    if (index < 0 || index > 3) {
      throw new Error("Choose a valid card position.");
    }

    const cards = [...entry.cards];
    cards[index] = { ...cards[index], knownToOwner: true };
    return { ...entry, cards };
  });

  return {
    ...state,
    players,
    lastMessage: `${state.players.find((p) => p.id === playerId)?.name} revealed card #${index + 1}.`,
  };
}

export function revealOwnWithNine(
  state: InternalGolfState,
  playerId: string,
  index: number,
): InternalGolfState {
  const pending = ensureActivePlayerPending(state, playerId);
  if (pending.card.rank !== 9) {
    throw new Error("Nine power card is required for a self-reveal.");
  }

  return revealOwnCard(state, playerId, index);
}

export function knock(state: InternalGolfState, playerId: string): InternalGolfState {
  ensureHostTurn(state, playerId);

  if (state.pending) {
    throw new Error("Finish your turn action before knocking.");
  }

  if (state.knockPlayerId) {
    throw new Error("Someone already knocked.");
  }

  const withKnock = {
    ...state,
    knockPlayerId: playerId,
    remainingTurnsAfterKnock: state.players.length,
    players: state.players.map((item) => {
      if (item.id === playerId) {
        return { ...item, knocked: true };
      }
      return item;
    }),
    lastMessage: `${state.players[state.turnIndex]?.name || "Player"} knocked! Everyone gets one more round.`,
  };

  return nextTurnState(withKnock);
}

export function calculateScores(players: GolfPlayer[]): PlayerScore[] {
  return players.map((player) => ({
    playerId: player.id,
    score: scoreCards(player.cards),
  }));
}

export function revealOpponentCard(
  state: InternalGolfState,
  playerId: string,
  targetPlayerId: string,
  index: number,
): PeekedCard {
  const player = state.players.find((entry) => entry.id === playerId);
  if (!player) {
    throw new Error("Player not found.");
  }

  const pending = ensureActivePlayerPending(state, playerId);
  if (pending.card.rank !== 8) {
    throw new Error("Eight power card is required for peek.");
  }

  if (index < 0 || index > 3) {
    throw new Error("Choose a valid card position.");
  }

  const target = state.players.find((entry) => entry.id === targetPlayerId);
  if (!target) {
    throw new Error("Target player not found.");
  }

  const targetSlot = target.cards[index];

  return {
    playerId: targetPlayerId,
    index,
    label: targetSlot.card.label,
    rank: targetSlot.card.rank,
    suit: targetSlot.card.suit,
  };
}

export function buildClientState(
  state: InternalGolfState,
  viewerId: string,
): GolfClientState {
  const revealAll = state.status === "finished";
  const players = state.players.map((player) => {
    const isMine = player.id === viewerId;
    const score = revealAll ? scoreCards(player.cards) : null;

    return {
      id: player.id,
      name: player.name,
      avatar: player.avatar,
      accent: player.accent,
      isHost: player.isHost,
      connected: player.connected,
      knocked: player.knocked,
      cards: player.cards.map((slot) => cardToClientView(slot, isMine, revealAll)),
      score,
    };
  });

  const pendingDrawnCard =
    state.pending?.playerId === viewerId ? state.pending?.card ?? null : null;

  return {
    roomCode: state.roomCode,
    status: state.status,
    players,
    turnPlayerId: state.players[state.turnIndex]?.id ?? null,
    drawnCardForMe: pendingDrawnCard,
    deckCount: state.deck.length,
    discardTop: state.discardPile[state.discardPile.length - 1] ?? null,
    discardCount: state.discardPile.length,
    pendingPlayerId: state.pending?.playerId ?? null,
    knockPlayerId: state.knockPlayerId,
    remainingTurnsAfterKnock: state.remainingTurnsAfterKnock,
    lastMessage: state.lastMessage,
  };
}

export function resetGame(state: InternalGolfState): InternalGolfState {
  const withDeck: InternalGolfState = {
    ...state,
    status: "lobby",
    players: state.players.map((player) => ({
      ...player,
      cards: player.cards.map((slot) => ({
        ...slot,
        knownToOwner: false,
      })),
      knocked: false,
    })),
    turnIndex: 0,
    deck: createFreshDeckPile(),
    discardPile: [],
    pending: null,
    knockPlayerId: null,
    remainingTurnsAfterKnock: null,
    lastMessage: "Ready to start again.",
  };

  return withDeck;
}
